// "Ääntämisen apu äänityksissä": the coach's features in the person's own recordings (docs/design/coach-research/
// recordings-plan.md, Phase 1; Axel's requirement of 2026-10-12). Every feature is optional per person and per
// feature, independent of the coach (UserInfo.coach) itself, and all are off for both by default. With every one
// off nothing changes anywhere. Stored per person as JSON in user.rec_coach (db/schema/010-people.sql), synced
// like user.coach (PATCH /api/me { recCoach }). Pure TS: the API validates with readRecCoachPrefs, the app reads
// the effective set (dependencies enforced) with effectiveRecCoach.

/**
 * The features, in the settings' order.
 *   clean     Puhdas otto: the take is recorded without the phone's voice processing (the coach's audio tap);
 *             still saved as AAC.
 *   hint      Äännevihje: after a take, at most one letter is dotted-underlined when the meter and the model agree.
 *   card      Näin se sanotaan: a collapsible articulation card above the record button.
 *   replay    Suukuva otosta: tap the underlined letter → the mouth replay with the honest ghost.
 *   better    Parempi otto: a quiet line in the two-take voicing (Lausepino) when the takes differ.
 *   focus     Harjoitellaan ääntä: the sound focus (two days of agreed misses in, two on-target takes out).
 *   earAsk    Roosan korva, the marker's side: I get at most three one-letter questions a day on the partner's takes.
 *   earMine   Roosan korva, the recorder's side: my takes may be asked about.
 */
export const REC_COACH_KEYS = ['clean', 'hint', 'card', 'replay', 'better', 'focus', 'earAsk', 'earMine'] as const;
export type RecCoachKey = (typeof REC_COACH_KEYS)[number];
export type RecCoachPrefs = Readonly<Record<RecCoachKey, boolean>>;

/** The rung of Äännevihje's attempts (rec-hint.ts): not a ladder step, the coach's ladders never count them. */
export const PASSIVE_RUNG = 'passive';

/** All off (both users' default). */
export const REC_COACH_OFF: RecCoachPrefs = Object.freeze({
  clean: false,
  hint: false,
  card: false,
  replay: false,
  better: false,
  focus: false,
  earAsk: false,
  earMine: false,
});

/**
 * What each feature needs on (rules as data). Äännevihje needs the unprocessed take (the meters are calibrated on
 * raw input: docs/design/coach-research/repo-mic-platform.md); the replay, the better-take line, the sound focus and
 * asking about my takes all read Äännevihje's verdicts. A feature whose need is off is off (effectiveRecCoach), and
 * the setting says why.
 */
export const REC_COACH_NEEDS: Readonly<Partial<Record<RecCoachKey, readonly RecCoachKey[]>>> = {
  hint: ['clean'],
  replay: ['hint'],
  better: ['hint'],
  focus: ['hint'],
  earMine: ['hint'],
};

/** Features that use the on-device phone model (the download row in Asetukset → Ääni shows for these). */
export const REC_COACH_MODEL: readonly RecCoachKey[] = ['hint'];
/** Features whose meters read the person's calibration (the setting links to it). */
export const REC_COACH_CALIBRATION: readonly RecCoachKey[] = ['hint'];

/** A stored or sent value as prefs: known keys with true stay on, anything else is off. */
export function readRecCoachPrefs(x: unknown): RecCoachPrefs {
  let v: unknown = x;
  if (typeof v === 'string') {
    try {
      v = JSON.parse(v);
    } catch {
      return REC_COACH_OFF;
    }
  }
  if (!v || typeof v !== 'object' || Array.isArray(v)) return REC_COACH_OFF;
  const o = v as Record<string, unknown>;
  const out = { ...REC_COACH_OFF } as Record<RecCoachKey, boolean>;
  for (const k of REC_COACH_KEYS) out[k] = o[k] === true;
  return out;
}

/** A PATCH body's recCoach: only known keys with boolean values, or null when it is not one. */
export function readRecCoachPatch(x: unknown): Partial<Record<RecCoachKey, boolean>> | null {
  if (!x || typeof x !== 'object' || Array.isArray(x)) return null;
  const o = x as Record<string, unknown>;
  const out: Partial<Record<RecCoachKey, boolean>> = {};
  for (const [k, v] of Object.entries(o)) {
    if (!(REC_COACH_KEYS as readonly string[]).includes(k) || typeof v !== 'boolean') return null;
    out[k as RecCoachKey] = v;
  }
  return out;
}

/** The stored form: only the keys that are on (an all-off person stores '{}'). */
export function storeRecCoach(p: RecCoachPrefs): string {
  const on: Partial<Record<RecCoachKey, true>> = {};
  for (const k of REC_COACH_KEYS) if (p[k]) on[k] = true;
  return JSON.stringify(on);
}

/** Whether `key` is blocked by a need that is off (directly or further up), and the first such need. */
export function blockedBy(p: RecCoachPrefs, key: RecCoachKey, seen: ReadonlySet<RecCoachKey> = new Set()): RecCoachKey | null {
  for (const need of REC_COACH_NEEDS[key] ?? []) {
    if (seen.has(need)) continue;
    if (!p[need]) return need;
    const up = blockedBy(p, need, new Set([...seen, key]));
    if (up) return up;
  }
  return null;
}

/** The features that actually run: a feature is on only when it and everything it needs are on. */
export function effectiveRecCoach(p: RecCoachPrefs): RecCoachPrefs {
  const out = {} as Record<RecCoachKey, boolean>;
  for (const k of REC_COACH_KEYS) out[k] = p[k] && blockedBy(p, k) === null;
  return out;
}

/** Any feature on at all (false: nothing of this changes anything for the person). */
export const anyRecCoach = (p: RecCoachPrefs): boolean => REC_COACH_KEYS.some((k) => p[k]);

/** The effective set uses the on-device model (the download row and the warm-up). */
export const recCoachUsesModel = (p: RecCoachPrefs): boolean => REC_COACH_MODEL.some((k) => effectiveRecCoach(p)[k]);
